import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useNotifications } from '../../context/NotificationContext';
import { Announcement } from '../../types/announcement';
import './AnnouncementsPage.css';
import { translateError } from '../../utils/errorMessages';

const AnnouncementsPage: React.FC = () => {
  const { t } = useTranslation();
  const { markAnnouncementsRead } = useNotifications();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnnouncements = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.announcements.my();
      setAnnouncements(data as Announcement[]);
      markAnnouncementsRead();
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  }, [markAnnouncementsRead]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="announcements-page">
        <h2>{t('announcements.title')}</h2>
        <div className="announcements-page__loading">
          <div className="announcements-page__spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="announcements-page">
      <h2>{t('announcements.title')}</h2>

      {error && <div className="announcements-page__error">{error}</div>}

      {announcements.length === 0 ? (
        <div className="announcements-page__empty">
          <p>{t('announcements.noAnnouncements')}</p>
        </div>
      ) : (
        <div className="announcements-page__list">
          {announcements.map((announcement) => (
            <div key={announcement._id} className="announcements-page__card">
              <div className="announcements-page__card-header">
                <div className="announcements-page__sender">
                  <div className="announcements-page__avatar">
                    {announcement.sender.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <span className="announcements-page__sender-name">
                      {announcement.sender.name}
                    </span>
                    <span className="announcements-page__time">
                      {formatTime(announcement.createdAt)}
                    </span>
                  </div>
                </div>
              </div>

              <p className="announcements-page__message">{announcement.message}</p>

              <div className="announcements-page__targets">
                {announcement.groups.length > 0 && (
                  <div className="announcements-page__target-group">
                    {announcement.groups.map((g) => (
                      <span key={g._id} className="announcements-page__tag announcements-page__tag--group">
                        {g.name}
                      </span>
                    ))}
                  </div>
                )}
                {announcement.events.length > 0 && (
                  <div className="announcements-page__target-group">
                    {announcement.events.map((e) => (
                      <span key={e._id} className="announcements-page__tag announcements-page__tag--event">
                        {e.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AnnouncementsPage;
